import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
const buttonVariants = cva("inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0", {
  variants: {
    variant: {
      default: "bg-primary/20 backdrop-blur-xl border border-primary/30 text-primary hover:bg-primary/30 hover:border-primary/50 shadow-lg shadow-primary/10 hover:shadow-primary/20 active:bg-primary/25",
      destructive: "bg-destructive/20 backdrop-blur-xl border border-destructive/30 text-destructive hover:bg-destructive/30 hover:border-destructive/50 shadow-lg shadow-destructive/10 active:bg-destructive/25",
      outline: "border border-border bg-transparent hover:bg-accent/10 hover:border-accent/50 text-foreground active:bg-accent/20",
      secondary: "bg-secondary/80 backdrop-blur-xl border border-border text-secondary-foreground hover:bg-secondary/90 active:bg-secondary",
      ghost: "hover:bg-accent/15 text-muted-foreground hover:text-foreground active:bg-accent/25",
      link: "text-primary underline-offset-4 hover:underline hover:text-primary/80",
      glass: "bg-card/80 backdrop-blur-xl border border-border text-foreground hover:bg-card/90 hover:border-accent/30 shadow-lg active:bg-card",
      hero: "bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:brightness-110 hover:scale-[1.02] active:scale-100",
      glow: "bg-primary/20 backdrop-blur-xl border border-primary/30 text-primary shadow-[0_0_20px_-5px] shadow-primary/40 hover:shadow-[0_0_30px_-5px] hover:shadow-primary/60 hover:bg-primary/30"
    },
    size: {
      default: "h-10 px-5 py-2",
      sm: "h-9 rounded-lg px-4 text-xs",
      lg: "h-12 rounded-xl px-8 text-base",
      xl: "h-14 rounded-2xl px-10 text-lg font-semibold",
      icon: "h-10 w-10"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant,
  size,
  asChild = false,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : "button";
  return <Comp className={cn(buttonVariants({
    variant,
    size,
    className
  }))} ref={ref} {...props} />;
});
Button.displayName = "Button";
export { Button, buttonVariants };